/**
 * Hook para o fluxo "Reportar esta tela" — abre o modal/drawer de criacao
 * de chamado pre-populado com o ScreenContext atual.
 *
 * Endpoint: POST /v1/chamados/from-screen (TASK-2461)
 *
 * Spec: §9.2.6 do CHAMADOS_VISIBILIDADE_SPEC.md
 *
 * Composicao:
 *  - useDisclosure (Mantine) controla opened/open/close
 *  - useMutation para enviar o payload
 *  - onSuccess: notification verde + invalida queries de chamados + close()
 *  - onError: notification vermelha preservando o `detail` original da API
 */

import { useDisclosure } from '@mantine/hooks'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { api } from '@/api/client'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'
import { formatApiError } from '@/api/errors'
import type { ScreenContext } from './useScreenContext'

/**
 * Forma do `screen_origin` que o servidor aceita — espelha `ScreenOriginInput`
 * em `src/consoleai/api.py`. Os limites de tamanho sao os do modelo Pydantic.
 */
export interface ScreenOriginPayload {
  screen_path: string
  screen_component?: string
  screen_label?: string
  error_signature?: string
  error_message?: string
  stack_trace?: string
  user_agent?: string
  viewport?: { w: number; h: number; dpr?: number }
  breadcrumbs?: string[]
  console_errors?: string[]
  client_app_version?: string
}

const corta = (v: string | undefined, teto: number) =>
  v ? v.slice(0, teto) : undefined

/**
 * Traduz o `ScreenContext` do frontend para o contrato do servidor.
 *
 * ESTE ADAPTADOR E' O CONSERTO de um defeito que viveu desde a TASK-2461: o
 * modal mandava o contexto CRU, e nenhum nome casava — `route_path` contra
 * `screen_path`, `viewport.width/height` contra `w/h`, `app_version` contra
 * `client_app_version`, `last_error` contra tres campos separados. O servidor
 * recusava com 422 `screen_path: Field required; w: Field required;
 * h: Field required`, e o botao "Reportar problema nesta tela" nunca criou
 * chamado nenhum.
 *
 * A traducao mora AQUI, na borda que fala com a API, e nao no
 * `useScreenContext`: o contexto serve tambem ao painel que a pessoa le na
 * tela (`ReportScreenContextPanel`), e esse painel nao tem por que usar o
 * vocabulario do banco.
 */
export function paraScreenOrigin(ctx: ScreenContext): ScreenOriginPayload {
  // `w`/`h` sao `ge=1` no servidor. Sem `window` o contexto traz 0, e mandar
  // zero seria trocar um 422 por outro — omitir e' o certo: o viewport e'
  // opcional.
  const viewport =
    ctx.viewport.width >= 1 && ctx.viewport.height >= 1
      ? {
          w: Math.min(ctx.viewport.width, 10000),
          h: Math.min(ctx.viewport.height, 10000),
          dpr: typeof window !== 'undefined' && window.devicePixelRatio
            ? Math.min(Math.max(window.devicePixelRatio, 0.1), 10)
            : 1.0,
        }
      : undefined

  return {
    screen_path: ctx.route_path.slice(0, 500),
    screen_component: corta(ctx.component, 200),
    screen_label: corta(ctx.route_label, 200),
    error_signature: corta(ctx.last_error?.error_id, 64),
    error_message: corta(ctx.last_error?.message, 500),
    stack_trace: corta(ctx.last_error?.component_stack, 8192),
    user_agent: corta(ctx.user_agent, 500),
    viewport,
    // Tetos de QUANTIDADE, nao de tamanho: o modelo aceita 10 e 5 itens. O
    // coletor ja limita nos mesmos numeros, entao isto e' cinto e suspensorio
    // — barato, e a divergencia custaria 422 sem mensagem util.
    breadcrumbs: ctx.breadcrumbs.slice(-10),
    console_errors: ctx.console_errors.slice(-5),
    client_app_version: corta(ctx.app_version, 50),
  }
}

export interface ReportScreenPayload {
  tenant_id: number
  title?: string
  description?: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
  category_id?: number
  /** Coletado por useScreenContext — traduzido por `paraScreenOrigin` no envio */
  screen_origin: ScreenContext
  /** Screenshot opcional (data URL ou base64 puro) */
  screenshot_base64?: string
}

interface ReportScreenResponse {
  id: number
  protocol_number: string
}

export function useReportScreen() {
  const [opened, { open, close }] = useDisclosure(false)
  const qc = useQueryClient()

  const mutation = useMutation<ReportScreenResponse, unknown, ReportScreenPayload>({
    mutationFn: async (payload) => {
      const res = await api.post<ReportScreenResponse>(
        '/v1/chamados/from-screen',
        // A traducao acontece AQUI, na borda: o chamador continua passando o
        // `ScreenContext`, e o servidor recebe o vocabulario dele.
        { ...payload, screen_origin: paraScreenOrigin(payload.screen_origin) },
      )
      return res.data
    },
    onSuccess: (data) => {
      invalidarPendenciasDoChamado(qc)
      notifications.show({
        title: 'Chamado criado',
        message: `Protocolo ${data.protocol_number} criado com sucesso`,
        color: 'green',
      })
      close()
    },
    onError: (err) => {
      notifications.show({
        title: 'Falha ao criar chamado',
        message: formatApiError(err, 'Erro ao criar chamado'),
        color: 'red',
      })
    },
  })

  return {
    opened,
    open,
    close,
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  }
}
